import type { StreamAnalysis, SegmentResult, HLSVariant, StreamTestPhase } from '../types/diagnostics';

const API_BASE = '';

export interface StreamProgress {
  phase: StreamTestPhase;
  analysis: StreamAnalysis | null;
  segments: SegmentResult[];
  variants: HLSVariant[];
  error: string | null;
}

type StreamProgressCallback = (progress: StreamProgress) => void;

/**
 * Video stream analyzer - tests HLS/DASH/generic video stream URLs
 */
export class StreamTester {
  private onProgress: StreamProgressCallback;
  private abortController: AbortController | null = null;
  private eventSource: EventSource | null = null;

  constructor(onProgress: StreamProgressCallback) {
    this.onProgress = onProgress;
  }

  abort(): void {
    this.abortController?.abort();
    this.eventSource?.close();
    this.abortController = null;
    this.eventSource = null;
  }

  async analyzeStream(url: string): Promise<StreamAnalysis | null> {
    this.abortController = new AbortController();

    const progress: StreamProgress = {
      phase: 'connecting',
      analysis: null,
      segments: [],
      variants: [],
      error: null,
    };

    this.onProgress({ ...progress });

    return new Promise((resolve) => {
      const encodedURL = encodeURIComponent(url);
      const es = new EventSource(`${API_BASE}/api/stream/check?url=${encodedURL}`);
      this.eventSource = es;

      // Timeout
      const timeout = setTimeout(() => {
        es.close();
        progress.phase = 'error';
        progress.error = 'Stream analysis timed out';
        this.onProgress({ ...progress });
        resolve(null);
      }, 65000);

      es.addEventListener('phase', (e) => {
        const data = JSON.parse(e.data);
        progress.phase = data.phase as StreamTestPhase;
        this.onProgress({ ...progress });
      });

      es.addEventListener('probe', (e) => {
        const data = JSON.parse(e.data);
        progress.analysis = {
          url,
          streamType: data.streamType,
          available: data.available,
          statusCode: data.statusCode,
          contentType: data.contentType,
          ttfbMs: data.ttfbMs,
          totalTimeMs: 0,
          bodySizeBytes: 0,
          bitrateKbps: 0,
          avgSegDurationMs: 0,
          bufferHealthPct: 0,
          qualityScore: 0,
          serverInfo: data.server,
          cdn: data.cdn,
          timestamp: Date.now(),
        };
        this.onProgress({ ...progress });
      });

      es.addEventListener('variants', (e) => {
        const data: HLSVariant[] = JSON.parse(e.data);
        progress.variants = data;
        this.onProgress({ ...progress });
      });

      es.addEventListener('segment', (e) => {
        const seg: SegmentResult = JSON.parse(e.data);
        progress.segments = [...progress.segments, seg];
        this.onProgress({ ...progress });
      });

      es.addEventListener('complete', (e) => {
        clearTimeout(timeout);
        const result: StreamAnalysis = JSON.parse(e.data);
        progress.phase = 'complete';
        progress.analysis = result;
        this.onProgress({ ...progress });
        es.close();
        resolve(result);
      });

      es.addEventListener('error', (e) => {
        clearTimeout(timeout);
        try {
          const data = JSON.parse((e as MessageEvent).data);
          progress.error = data.error || 'Stream analysis failed';
        } catch {
          progress.error = 'Connection to server lost';
        }
        progress.phase = 'error';
        this.onProgress({ ...progress });
        es.close();
        resolve(null);
      });

      es.onerror = () => {
        clearTimeout(timeout);
        if (progress.phase !== 'complete' && progress.phase !== 'error') {
          progress.phase = 'error';
          progress.error = 'Connection to server lost';
          this.onProgress({ ...progress });
          es.close();
          resolve(null);
        }
      };

      // Handle abort
      this.abortController?.signal.addEventListener('abort', () => {
        clearTimeout(timeout);
        es.close();
        progress.phase = 'idle';
        this.onProgress({ ...progress });
        resolve(null);
      });
    });
  }

  /**
   * Simple non-streaming analysis via POST
   */
  async analyzeStreamSimple(url: string): Promise<StreamAnalysis | null> {
    this.abortController = new AbortController();
    
    const progress: StreamProgress = {
      phase: 'connecting',
      analysis: null,
      segments: [],
      variants: [],
      error: null,
    };
    this.onProgress({ ...progress });

    try {
      const resp = await fetch(`${API_BASE}/api/stream/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
        signal: this.abortController.signal,
      });

      const result: StreamAnalysis = await resp.json();
      progress.phase = 'complete';
      progress.analysis = result;
      progress.segments = result.segments || [];
      progress.variants = result.variants || [];
      this.onProgress({ ...progress });
      return result;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        progress.phase = 'idle';
      } else {
        progress.phase = 'error';
        progress.error = err instanceof Error ? err.message : 'Unknown error';
      }
      this.onProgress({ ...progress });
      return null;
    }
  }

  static downloadReport(analysis: StreamAnalysis): void {
    const json = JSON.stringify(analysis, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stream-report-${new Date().toISOString().slice(0, 19)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
